import { useEffect, useRef } from "react";
import { useVadRecorder, injectLocalSTTControls } from "./useVadRecorder";
import { useVoiceStore } from "@/store/useVoiceStore";
import { sendAIChatMessage } from "@/api/aiChat";
import { useChatSessionStore } from "@/store/useChatSessionStore";
import { getTime } from "@/pages/ai-voice/engine";

// ─── 浏览器原生语音识别（SpeechRecognition）封装 ──────────
// 兼容 Android Chrome 和 iOS Safari，解决 iOS 上服务端 STT 不返回转写文字的问题

type SRInstance = {
  /** 停止识别并返回最终转写文字（异步，确保拿到最后的结果） */
  stop: () => Promise<string>;
  /** 强制终止 */
  abort: () => void;
  /** 标记从此刻起只保留新的转写结果（VAD模式：检测到说话时调用） */
  mark: () => void;
} | null;

/** 检测是否为 iOS 设备 */
function isIOS(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/** 检测浏览器是否支持 SpeechRecognition */
function isSpeechRecognitionSupported(): boolean {
  return !!(
    typeof window !== "undefined" &&
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)
  );
}

/**
 * 创建一个语音识别实例（与录音并行运行）
 *
 * ★ iOS 兼容性处理：
 *   - iOS 上使用 non-continuous 模式 + 手动重启（更稳定）
 *   - stop() 返回 Promise，确保异步等到最后一个 onresult 结果
 *   - 同时收集 interimResults 和 finalResults，优先返回 final
 */
function createSpeechRecognition(): SRInstance {
  if (!isSpeechRecognitionSupported()) return null;

  const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  const recognizer = new SR();

  // ★ iOS 使用非连续模式（更稳定），Android 用连续模式
  const _isIOS = isIOS();
  recognizer.continuous = !_isIOS;       // iOS: false, Android: true
  recognizer.interimResults = true;        // 两端都开启 interim
  recognizer.lang = "zh-CN";
  recognizer.maxAlternatives = 1;

  let finalTranscript = "";     // 最终确认的文字
  let interimTranscript = "";   // 临时文字（作为兜底）
  let markResultIndex = 0;      // mark() 截断点
  let active = true;
  let resolveStop: ((text: string) => void) | null = null;
  let stopped = false;          // 是否已调用 stop()

  // ★ iOS 非连续模式：自动重启逻辑
  let restartTimer: ReturnType<typeof setTimeout> | null = null;

  recognizer.onresult = (event: any) => {
    if (!active) return;

    let hasNewFinal = false;
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (result.isFinal && result[0]) {
        finalTranscript += result[0].transcript;
        hasNewFinal = true;
      } else if (result[0]) {
        // 更新最新的 interim 文字（只保留最新一段）
        interimTranscript = result[0].transcript;
      }
    }

    if (hasNewFinal) {
      console.log(`[STT-local] ✅ final: "${event.results[event.resultIndex]?.[0]?.transcript}"`);
    }
    console.log(`[STT-local] interim: "${interimTranscript}", final="${finalTranscript}"`);
  };

  recognizer.onerror = (event: any) => {
    const err = event.error || "unknown";
    console.warn(`[STT-local] ❌ 错误: ${err}`);

    // no-speech / aborted 属于正常情况
    if (err === "no-speech" || err === "aborted") return;

    // iOS 常见错误：network / service-not-allowed / not-allowed
    active = false;

    // 如果正在等待 stop() 结果，立即 resolve
    if (resolveStop) {
      const text = (finalTranscript || interimTranscript).trim();
      resolveStop(text);
      resolveStop = null;
    }
  };

  recognizer.onend = () => {
    // 清理自动重启定时器
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }

    // 被外部 stop()/abort() 触发的正常结束
    if (!active || stopped) {
      if (resolveStop) {
        // ★ 关键：即使没有 final 结果，也把 interim 结果作为兜底返回
        const text = (finalTranscript || interimTranscript).trim();
        console.log(`[STT-local] onend → 返回: "${text || '(无)'}"`);
        resolveStop(text);
        resolveStop = null;
      }
      return;
    }

    // ★ iOS 非连续模式：自动重启（模拟 continuous 行为）
    if (_isIOS && active) {
      restartTimer = setTimeout(() => {
        if (active && !stopped) {
          try {
            recognizer.start();
            console.log("[STT-local] iOS 自动重启识别");
          } catch (e) {
            console.warn("[STT-local] iOS 重启失败:", e);
            active = false;
          }
        }
      }, 100); // 100ms 后重启
    }
  };

  try {
    recognizer.start();
    console.log(`[STT-local] 🎙️ 已启动 (${_isIOS ? "iOS-非连续" : "Android-连续"}模式)`);
  } catch (e) {
    console.warn("[STT-local] 启动失败:", e);
    return null;
  }

  return {
    stop: () => {
      return new Promise<string>((resolve) => {
        active = false;
        stopped = true;

        // 清理自动重启定时器
        if (restartTimer) {
          clearTimeout(restartTimer);
          restartTimer = null;
        }

        resolveStop = resolve;

        // 给一个安全超时（500ms），防止 onend/onresult 永远不来
        const safetyTimeout = setTimeout(() => {
          if (resolveStop) {
            const text = (finalTranscript || interimTranscript).trim();
            console.log(`[STT-local] ⏱️ 安全超时返回: "${text || '(无)'}"`);
            resolveStop(text);
            resolveStop = null;
          }
        }, 800);

        try { recognizer.stop(); } catch {}

        // 当 resolve 被调用时清除超时
        const origResolve = resolve;
        resolve = (text: string) => {
          clearTimeout(safetyTimeout);
          origResolve(text);
        };
        resolveStop = resolve;
      });
    },
    abort: () => {
      active = false;
      stopped = true;
      if (restartTimer) { clearTimeout(restartTimer); restartTimer = null; }
      if (resolveStop) { resolveStop(""); resolveStop = null; }
      try { recognizer.abort(); } catch {}
    },
    /** 标记：从此刻起只保留新的转写结果 */
    mark: () => {
      const results = (recognizer as any).results;
      markResultIndex = results ? results.length : 0;
      finalTranscript = "";
      interimTranscript = "";
      console.log(`[STT-local] ✂️ 已标记截断点, index=${markResultIndex}`);
    },
  };
}

// ─── 全局 SpeechRecognition 实例（模块级单例）────────────
let _currentSR: SRInstance = null;

/** 启动本地语音识别（与录音并行） */
export function startLocalSTT(): boolean {
  try { _currentSR?.abort(); } catch {}
  _currentSR = createSpeechRecognition();
  const ok = _currentSR !== null;
  console.log(`[STT-local] startLocalSTT → ${ok ? "成功" : "失败(浏览器不支持SpeechRecognition)"}`);
  return ok;
}

/** 停止本地语音识别并返回转写文字（异步，确保拿到最后结果） */
export async function stopLocalSTT(): Promise<string> {
  if (!_currentSR) return "";
  const sr = _currentSR;
  _currentSR = null;
  return sr.stop();
}

/** 强制中止本地语音识别 */
export function abortLocalSTT(): void {
  if (_currentSR) {
    _currentSR.abort();
    _currentSR = null;
  }
}

/** 标记截断点：从此刻起只保留新的转写结果（VAD模式检测到说话时调用） */
export function markLocalSTT(): void {
  _currentSR?.mark();
}

/** 麦克风错误提示文案映射 */
const MIC_ERROR_MESSAGES: Record<string, string> = {
  permission_denied: "请允许麦克风权限以使用语音功能",
  not_found: "未检测到麦克风设备",
  device_error: "麦克风启动失败，请检查设备设置",
  unknown: "语音功能异常，请重试或刷新页面",
};

export default function VoiceRuntime() {
  const {
    vadState,
    isListening,
    isPressRecording,
    deviceStatus,
    waveData,
    audioSilent,
    isAudioProcessing,
    startListening,
    stopListening,
    startPressToRecord,
    stopPressToRecord,
    finishAudioProcessing,
  } = useVadRecorder({
    silenceThreshold: 10,
    silenceDurationMs: 2000,
    onError: (errorType, message) => {
      console.warn(`[voice-runtime] mic error [${errorType}]: ${message}`);
      const userMsg = MIC_ERROR_MESSAGES[errorType] || MIC_ERROR_MESSAGES.unknown;
      useVoiceStore.getState().setMicError({ errorType, message: userMsg, timestamp: Date.now() });
      // 尝试显示原生 toast 提示用户
      try {
        // uni.showToast 兼容（如有），否则 fallback 到 alert
        if (typeof (globalThis as any).uni?.showToast === "function") {
          (globalThis as any).uni.showToast({
            title: userMsg,
            icon: "none",
            duration: 3000,
          });
        }
      } catch {}
    },
    onAudioRecorded: async (blob) => {
      const audioUrl = URL.createObjectURL(blob);
      // ★ 获取本地语音识别的转写文字（异步等待最终结果，跨平台兼容 iOS/Android）
      const localTranscript = await stopLocalSTT();

      // ★ STT 调试日志：确认手机上本地识别是否正常工作
      console.log(`[voice-runtime] stopLocalSTT 返回: "${localTranscript || '(空)'}", 长度: ${localTranscript?.length ?? 0}`);

      // ★ 提前声明文件名，方便 catch 中打印日志
      const randomId = crypto.randomUUID().replace(/-/g, "").slice(0, 16);
      const ts = Date.now().toString();
      const voiceFileName = `voice_${randomId}.${ts}.webm`;

      // ★ 提前构建请求参数（catch 中可访问）
      const reqSessionId = useChatSessionStore.getState().session_id;

      // ★ 用 File/FormData 方式发送音频（与 ai-voice 页面 send() 一致，后端接受 FormData 上传）
      const audioFile = new File([blob], voiceFileName, {
        type: blob.type || "audio/webm",
      });

      try {
        console.log(`[voice-runtime] 发送音频到服务端(FormData-File), fileName: ${voiceFileName}, size: ${blob.size} bytes, type: ${blob.type}`);
        await sendAIChatMessage({
          type: "audio",
          content: audioFile,
          session_id: reqSessionId || undefined,
        });

        // 发送成功，添加用户语音气泡
        // ★ 优先使用本地转写文字；为空时显示 🎤 占位（App.tsx SSE 处理会用服务端 STT 结果替换此占位）
        const displayText = localTranscript?.trim() || "\u{1F3A4}";
        const { appendConversationMessages } = useVoiceStore.getState();
        appendConversationMessages([
          {
            role: "user",
            text: displayText,
            time: getTime(),
            isVoice: true,
            url: audioUrl,
          } as Omit<import("@/pages/ai-voice/types").Msg, "id">,
        ]);

        console.log(`[voice-runtime] 语音已发送, 显示文字: "${displayText}", 本地转写原始值: "${localTranscript || '(无)'}", 等待SSE推送AI回复`);
      } catch (error: any) {
        // ★ 收集完整错误信息 + 请求参数
        const status = error?.response?.status;
        const serverData = error?.response?.data;
        const errMsg = error instanceof Error ? error.message : "语音发送失败";

        // 完整请求参数快照
        const reqParams = {
          type: "audio",
          fileName: voiceFileName,
          fileSize: blob.size,
          fileType: blob.type || "(unknown)",
          session_id: reqSessionId || "(空)",
          localTranscript: localTranscript || "(无转写)",
        };

        // ★★ 完整打印后端返回信息（含响应体、响应头、请求头）★★
        let serverDataStr = "(无响应体)";
        try {
          serverDataStr =
            typeof serverData === "string"
              ? serverData
              : JSON.stringify(serverData, null, 2) ?? "(无响应体)";
        } catch {
          serverDataStr = `[序列化失败] ${String(serverData)}`;
        }

        // ★ 完整响应体打印到调试面板（手机上看不到 console，用浮层面板展示）
        const dbgResp = [
          `❌ 发送失败 HTTP ${status}`,
          `· message: ${errMsg}`,
          `· fileName: ${voiceFileName}`,
          `· size: ${blob.size}B  type: ${blob.type || "(unknown)"}`,
          `· 响应体: ${serverDataStr}`,
          `· 响应头: ${JSON.stringify(error?.response?.headers ?? {})}`,
          `· 请求头(Chat-Token/Chat-Session): 见Network`,
        ].join("\n");
        console.error("[voice-runtime] 完整错误信息 >>>\n", dbgResp);
        console.error("[voice-runtime] 原始 error 对象:", error);

        // ★ 把响应体透传到浮层调试面板显示（最长截断 2000 字符防刷屏）
        try {
          useVoiceStore.getState().setMicError({
            errorType: "device_error",
            message: dbgResp.slice(0, 2000),
            timestamp: Date.now(),
          });
        } catch {}

        console.error("[voice-runtime] send audio failed:", { status, serverData, message: errMsg, reqParams });

        // ★ 设置横幅提示
        let userMsg = errMsg;
        if (/status\s*code\s*(500|502|503|504)/.test(errMsg)) {
          userMsg = "服务繁忙，请稍后再试";
        } else if (errMsg.includes("timeout")) {
          userMsg = "语音识别超时，请检查网络后重试";
        }
        useVoiceStore.getState().setMicError({
          errorType: "device_error",
          message: userMsg,
          timestamp: Date.now(),
        });
      } finally {
        finishAudioProcessing();
      }
    },
  });

  // 同步 VAD 运行时状态到全局 store
  useEffect(() => {
    useVoiceStore.getState().setVadRuntimeState({
      vadState,
      isListening,
      isPressRecording,
      deviceStatus,
      waveData,
      audioSilent,
      isAudioProcessing,
    });
  }, [
    vadState,
    isListening,
    isPressRecording,
    deviceStatus,
    waveData,
    audioSilent,
    isAudioProcessing,
  ]);

  // 注册 VAD 操作到 store，让页面可以调用
  useEffect(() => {
    const unregister = useVoiceStore.getState().registerVadActions({
      startListening,
      stopListening,
      startPressToRecord,
      stopPressToRecord,
      finishAudioProcessing,
    });
    return unregister;
  }, [startListening, stopListening, startPressToRecord, stopPressToRecord, finishAudioProcessing]);

  // ── 按住录音模式下不自动启动 VAD 监听，由用户按住按钮时手动触发 ──
  useEffect(() => {
    useVoiceStore.getState().setAutoStarted(true);
  }, []);

  // ★ 注入 SpeechRecognition 控制函数到 VAD Recorder（让录音启动/取消时同步启停语音识别）
  useEffect(() => {
    injectLocalSTTControls(startLocalSTT, abortLocalSTT, markLocalSTT);
  }, []);

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  return null;
}
